'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { TrendingUp } from 'lucide-react';
import { FilterGroup } from '../FilterGroup';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import {
  AktivaField,
  EmployeesField,
  FinancialYearField,
  HasFilingsField,
  ProfitField,
  RevenueField,
} from './SizeFilterGroupParts';

export interface SizeFilterGroupProps {
  t: TFunction<'translation', undefined>;
  sizeCount: number;
  clearSize: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | null;
  lockOf: (control: string) => 'user' | null;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  extents: {
    revenue_max: number;
    aktiva_max: number;
    profit_min: number;
    profit_max: number;
    employees_max: number;
  };
  showFinancialYear: boolean;
  yearPickerOpen: boolean;
  setYearPickerOpen: Dispatch<SetStateAction<boolean>>;
  yearOptions: number[];
  facetCount: (facet: string, value: string) => number | undefined;
}

export function SizeFilterGroup(props: SizeFilterGroupProps) {
  const {
    t,
    sizeCount,
    clearSize,
    openFilterGroup,
    handleGroupOpenChange,
    groupLock,
    showFinancialYear,
  } = props;
  return (
    <FilterGroup
      id="size"
      icon={TrendingUp}
      label={t('search.sidebar.groups.size', {
        defaultValue: 'Size & financials',
      })}
      activeCount={sizeCount}
      onClear={clearSize}
      open={openFilterGroup === 'size'}
      onOpenChange={handleGroupOpenChange('size')}
      lock={groupLock('size')}
    >
      <RevenueField {...props} />
      <AktivaField {...props} />
      <ProfitField {...props} />
      <EmployeesField {...props} />
      {/* Financial year — only rendered when the parent exposes the year
          setter; otherwise the picker is meaningless. */}
      {showFinancialYear && <FinancialYearField {...props} />}
      <HasFilingsField {...props} />
      {/* A new size/financial filter: a field component in
          SizeFilterGroupParts.tsx reading staged.<key> / set.<key>
          (RangeSliderFilter for a range, FilterCheckbox for a boolean),
          rendered here in sidebar order. */}
    </FilterGroup>
  );
}
